import { useState } from 'react';
import { Modal, Image, Button, Icon, Segment, Label, Loader, Grid } from 'semantic-ui-react';
import { getAPI } from '../libs/fetchAPI';

function ArtifactViewer({ artifacts }) {
	const [selectedArtifact, setSelectedArtifact] = useState(null);
	const [artifactUrl, setArtifactUrl] = useState(null);
	const [loading, setLoading] = useState(false);

	if (!artifacts || artifacts.length === 0) {
		return (
			<Segment placeholder>
				<Icon name="file outline" size="huge" disabled />
				<p>No artifacts available for this suite</p>
			</Segment>
		);
	}

	const screenshots = artifacts.filter(a => a.artifact_type === 'screenshot');
	const videos      = artifacts.filter(a => a.artifact_type === 'video');

	const handleArtifactClick = async (artifact) => {
		setSelectedArtifact(artifact);
		setLoading(true);

		try {
			const response = await getAPI(`/artifacts/${artifact.id}/download`);
			setArtifactUrl(response.url);
		}
		catch (error) {
			console.error('Failed to get artifact URL:', error);
		}
		finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setSelectedArtifact(null);
		setArtifactUrl(null);
	};

	return (
		<>
			{screenshots.length > 0 && (
				<Segment>
					<Label attached="top">
						<Icon name="image" /> Screenshots ({screenshots.length})
					</Label>
					<Grid columns={4} doubling stackable style={{ marginTop: '10px' }}>
						{screenshots.map(artifact => (
							<Grid.Column key={artifact.id}>
								<Segment
									onClick={() => handleArtifactClick(artifact)}
									style={{
										cursor       : 'pointer',
										textAlign    : 'center',
										padding      : '20px',
										minHeight    : '100px',
										display      : 'flex',
										alignItems   : 'center',
										justifyContent : 'center',
										flexDirection  : 'column'
									}}
								>
									<Icon name="image" size="big" color="grey" />
									<p style={{ marginTop: '10px', fontSize: '12px', wordBreak: 'break-all' }}>
										{artifact.filename}
									</p>
								</Segment>
							</Grid.Column>
						))}
					</Grid>
				</Segment>
			)}

			{videos.length > 0 && (
				<Segment>
					<Label attached="top">
						<Icon name="video" /> Videos ({videos.length})
					</Label>
					<Grid columns={4} doubling stackable style={{ marginTop: '10px' }}>
						{videos.map(artifact => (
							<Grid.Column key={artifact.id}>
								<Segment
									onClick={() => handleArtifactClick(artifact)}
									style={{
										cursor       : 'pointer',
										textAlign    : 'center',
										padding      : '20px',
										minHeight    : '100px',
										display      : 'flex',
										alignItems   : 'center',
										justifyContent : 'center',
										flexDirection  : 'column'
									}}
								>
									<Icon name="video" size="big" color="grey" />
									<p style={{ marginTop: '10px', fontSize: '12px', wordBreak: 'break-all' }}>
										{artifact.filename}
									</p>
								</Segment>
							</Grid.Column>
						))}
					</Grid>
				</Segment>
			)}

			<ArtifactModal
				artifact={selectedArtifact}
				url={artifactUrl}
				loading={loading}
				onClose={handleClose}
			/>
		</>
	);
}

function ArtifactModal({ artifact, url, loading, onClose }) {
	if (!artifact) return null;

	const isVideo = artifact.artifact_type === 'video';

	return (
		<Modal
			size="large"
			open={!!artifact}
			onClose={onClose}
			closeIcon
		>
			<Modal.Header>
				<Icon name={isVideo ? 'video' : 'image'} />
				{artifact.filename}
			</Modal.Header>

			<Modal.Content>
				{loading ? (
					<div style={{ textAlign: 'center', padding: '50px' }}>
						<Loader active inline="centered">Loading artifact...</Loader>
					</div>
				) : isVideo ? (
					<video
						controls
						autoPlay
						style={{ width: '100%', maxHeight: '70vh' }}
					>
						<source src={url} type={artifact.mime_type} />
						Your browser does not support video playback.
					</video>
				) : (
					<Image
						src={url}
						fluid
						style={{ maxHeight: '70vh', objectFit: 'contain' }}
					/>
				)}
			</Modal.Content>

			<Modal.Actions>
				<Button
					as="a"
					href={url}
					download={artifact.filename}
					primary
					disabled={loading}
				>
					<Icon name="download" /> Download
				</Button>
				<Button onClick={onClose}>Close</Button>
			</Modal.Actions>
		</Modal>
	);
}

export default ArtifactViewer;
